import { API_BASE_URL, apiClient } from '@/services/api-client';

const MAX_LICENSE_LEAFLET_BYTES = 12 * 1024 * 1024;
const BASE64_CHUNK = 0x2000;

function leafletUrl(blobName: string): string {
  return `${API_BASE_URL}/ops/license-leaflets?name=${encodeURIComponent(blobName)}`;
}

async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += BASE64_CHUNK) {
    const slice = bytes.subarray(offset, Math.min(offset + BASE64_CHUNK, bytes.length));
    binary += String.fromCharCode(...slice);
  }
  return btoa(binary);
}

export async function replaceLicenseLeaflet(blobName: string, file: File): Promise<void> {
  const filename = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  if (!filename.endsWith('.pdf') && type !== 'application/pdf') {
    throw new Error('El fichero tiene que ser un PDF');
  }
  if (file.size <= 0) throw new Error('El PDF está vacío');
  if (file.size > MAX_LICENSE_LEAFLET_BYTES) throw new Error('El PDF supera 12 MB');

  const pdfBase64 = await fileToBase64(file);
  const { error } = await apiClient.put<{ blobName: string; contentLength: number }>(
    `/ops/license-leaflets?name=${encodeURIComponent(blobName)}`,
    { pdfBase64 }
  );
  if (error) throw new Error(error.message);
}

export async function deleteLicenseLeaflet(blobName: string): Promise<void> {
  const { error } = await apiClient.delete(`/ops/license-leaflets?name=${encodeURIComponent(blobName)}`);
  if (error) throw new Error(error.message);
}

export async function openLicenseLeaflet(blobName: string): Promise<void> {
  const token = await apiClient.getValidToken();
  if (!token) throw new Error('Sesión no válida');

  const preview = window.open('about:blank', '_blank');
  try {
    const res = await fetch(leafletUrl(blobName), {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      let message = 'No se pudo abrir el folleto';
      try {
        const err = (await res.json()) as { error?: string; message?: string };
        message = err.error || err.message || message;
      } catch {
        /* el cuerpo no era JSON */
      }
      throw new Error(message);
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    if (preview) {
      preview.location.replace(objectUrl);
    } else {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = blobName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    preview?.close();
    throw error;
  }
}
